import { useEffect, lazy, Suspense } from "react"; // build-v3
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";

import { scrollToProjectHash } from "@/utils/projectAnchors";

// Critical routes — stay in main bundle
import Index from "./pages/Index";
import Installation from "./pages/Installation";
import Refinishing from "./pages/Refinishing";
import RefinishingCost from "./pages/RefinishingCost";
import InstallationCost from "./pages/InstallationCost";
import Inspections from "./pages/Inspections";
import SubfloorLeveling from "./pages/SubfloorLeveling";
import ProductSpecification from "./pages/ProductSpecification";
import EnvironmentAcclimation from "./pages/EnvironmentAcclimation";
import Commercial from "./pages/Commercial";
import StaircaseRenovations from "./pages/StaircaseRenovations";
import LVPInstallation from "./pages/LVPInstallation";
import WaterproofLaminateInstallation from "./pages/WaterproofLaminateInstallation";
import AlternaInstallation from "./pages/AlternaInstallation";
import LuxuryVinylPlank from "./pages/LuxuryVinylPlank";
import LaminateFlooring from "./pages/LaminateFlooring";
import HardwoodFlooring from "./pages/HardwoodFlooring";
import ReclaimedBarnwood from "./pages/ReclaimedBarnwood";
import CustomFlooring from "./pages/CustomFlooring";
import NotFound from "./pages/NotFound";
import LegacyRedirectPage from "./pages/LegacyRedirectPage";

// Lazy-loaded: Ryze article system (/blog)
const BlogIndex = lazy(() => import("./pages/BlogIndex"));
const ArticlePage = lazy(() => import("./pages/ArticlePage"));

// Lazy-loaded: Brand pages
const BrandCali = lazy(() => import("./pages/BrandCali"));
const CaliCollectionPage = lazy(() => import("./pages/CaliCollection"));
const BrandViking = lazy(() => import("./pages/BrandViking"));
const BrandVikingBigSky = lazy(() => import("./pages/BrandVikingBigSky"));
const BrandVikingModernLoft = lazy(() => import("./pages/BrandVikingModernLoft"));
const BrandVikingUrbanRustic = lazy(() => import("./pages/BrandVikingUrbanRustic"));
const BrandIndusparquet = lazy(() => import("./pages/BrandIndusparquet"));
const IndusparquetClassico = lazy(() => import("./pages/IndusparquetClassico"));
const IndusparquetLargo = lazy(() => import("./pages/IndusparquetLargo"));
const IndusparquetNovo = lazy(() => import("./pages/IndusparquetNovo"));
const IndusparquetSolido = lazy(() => import("./pages/IndusparquetSolido"));
const IndusparquetValor = lazy(() => import("./pages/IndusparquetValor"));
const BrandLauzon = lazy(() => import("./pages/BrandLauzon"));
const LauzonCollection = lazy(() => import("./pages/LauzonCollection"));
const LauzonExpert = lazy(() => import("./pages/LauzonExpert"));
const BrandMercier = lazy(() => import("./pages/BrandMercier"));
const MercierAtmosphere = lazy(() => import("./pages/MercierAtmosphere"));
const MercierDesignPlus = lazy(() => import("./pages/MercierDesignPlus"));
const MercierElegancia = lazy(() => import("./pages/MercierElegancia"));
const MercierElement = lazy(() => import("./pages/MercierElement"));
const MercierEmblem = lazy(() => import("./pages/MercierEmblem"));
const MercierHerringbone = lazy(() => import("./pages/MercierHerringbone"));
const MercierNaked = lazy(() => import("./pages/MercierNaked"));
const MercierPro = lazy(() => import("./pages/MercierPro"));
const MercierOrigins = lazy(() => import("./pages/MercierOrigins"));
const MercierStellar = lazy(() => import("./pages/MercierStellar"));
const MercierSource = lazy(() => import("./pages/MercierSource"));
const BrandOldeWood = lazy(() => import("./pages/BrandOldeWood"));
const OldeWoodReclaimed = lazy(() => import("./pages/OldeWoodReclaimed"));
const OldeWoodTraditional = lazy(() => import("./pages/OldeWoodTraditional"));
const OldeWoodMetro = lazy(() => import("./pages/OldeWoodMetro"));
const OldeWoodMagnolia = lazy(() => import("./pages/OldeWoodMagnolia"));
const OldeWoodVenture = lazy(() => import("./pages/OldeWoodVenture"));
const OldeWoodEuropean = lazy(() => import("./pages/OldeWoodEuropean"));
const OldeWoodSelectPrime = lazy(() => import("./pages/OldeWoodSelectPrime"));
const OldeWoodRiftQuartered = lazy(() => import("./pages/OldeWoodRiftQuartered"));
const OldeWoodInspiration = lazy(() => import("./pages/OldeWoodInspiration"));
const OldeWoodBeyond = lazy(() => import("./pages/OldeWoodBeyond"));
const BrandIronwood = lazy(() => import("./pages/BrandIronwood"));
const BrandAlterna = lazy(() => import("./pages/BrandAlterna"));
const BrandQuickStep = lazy(() => import("./pages/BrandQuickStep"));
const BrandBellaCera = lazy(() => import("./pages/BrandBellaCera"));
const BellaCeraVillaBorgheseII = lazy(() => import("./pages/BellaCeraVillaBorgheseII"));
const QuickStepStudio = lazy(() => import("./pages/QuickStepStudio"));
const QuickStepNatureTEKPlus = lazy(() => import("./pages/QuickStepNatureTEKPlus"));
const QuickStepNatureTEKSelect = lazy(() => import("./pages/QuickStepNatureTEKSelect"));
const QuickStepNatureTEKPremier = lazy(() => import("./pages/QuickStepNatureTEKPremier"));
const DreamweaverCarpet = lazy(() => import("./pages/DreamweaverCarpet"));

// Lazy-loaded: Project pages
const Projects = lazy(() => import("./pages/Projects"));
const ProjectAnnArbor = lazy(() => import("./pages/ProjectAnnArbor"));
const ProjectAnnArborWhiteOak = lazy(() => import("./pages/ProjectAnnArborWhiteOak"));
const ProjectAnnArborTech = lazy(() => import("./pages/ProjectAnnArborTech"));
const ProjectLivonia = lazy(() => import("./pages/ProjectLivonia"));
const ProjectBrightonReclamation = lazy(() => import("./pages/ProjectBrightonReclamation"));
const ProjectLexingtonReclaimedOak = lazy(() => import("./pages/ProjectLexingtonReclaimedOak"));
const ProjectBrightonMercierHickory = lazy(() => import("./pages/ProjectBrightonMercierHickory"));
const ProjectBirmingham = lazy(() => import("./pages/ProjectBirmingham"));
const ProjectPlymouth = lazy(() => import("./pages/ProjectPlymouth"));
const ProjectNorthvilleWalnut = lazy(() => import("./pages/ProjectNorthvilleWalnut"));
const ProjectHarborSprings = lazy(() => import("./pages/ProjectHarborSprings"));

// Lazy-loaded: City pages
const CityPage = lazy(() => import("./pages/CityPage"));

// Lazy-loaded: Legal / misc
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const WaterDamageFlooring = lazy(() => import("./pages/WaterDamageFlooring"));
const SandAndFinish = lazy(() => import("./pages/SandAndFinish"));
const FloorRestoration = lazy(() => import("./pages/FloorRestoration"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const SeoIssuesDashboard = lazy(() => import("./pages/SeoIssuesDashboard"));

const queryClient = new QueryClient();

const ScrollToHash = () => {
  const { hash, pathname } = useLocation();
  useEffect(() => {
    if (hash) return scrollToProjectHash(hash);
    window.scrollTo(0, 0);
  }, [hash, pathname]);
  return null;
};

/**
 * Writes the current route path onto <html data-route="..."> after each
 * route's component mounts. The prerender script (scripts/prerender.mjs)
 * waits on this marker matching the expected path before snapshotting,
 * which prevents capturing the homepage body for a non-home route.
 *
 * IMPORTANT: This component is rendered INSIDE <Suspense> so that it only
 * mounts after the lazy-loaded route chunk has resolved. The Suspense
 * fallback (a placeholder div) does not include this component, so the
 * marker is never written prematurely while a chunk is still loading.
 */
const RouteMarker = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    // Defer two frames so the route component has actually painted
    // (lazy chunk resolved + React committed + browser painted) before
    // we signal "ready" to Puppeteer.
    const r1 = requestAnimationFrame(() => {
      const r2 = requestAnimationFrame(() => {
        document.documentElement.dataset.route = pathname === "/projects/howell-reclaimed-tobacco-pine" ? `https://1woodfloors.com${pathname}` : pathname;
      });
      // Store inner id on outer for cleanup
      (document.documentElement as any).__rafId = r2;
    });
    return () => {
      cancelAnimationFrame(r1);
      const r2 = (document.documentElement as any).__rafId;
      if (r2) cancelAnimationFrame(r2);
    };
  }, [pathname]);
  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
          <ScrollToHash />
          <Suspense fallback={<div className="min-h-screen" />}>
          <RouteMarker />
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/brands/viking" element={<BrandViking />} />
            <Route path="/brands/viking/big-sky" element={<BrandVikingBigSky />} />
            <Route path="/brands/viking/modern-loft" element={<BrandVikingModernLoft />} />
            <Route path="/brands/viking/urban-rustic" element={<BrandVikingUrbanRustic />} />
            <Route path="/brands/cali" element={<BrandCali />} />
            <Route path="/brands/cali/:slug" element={<CaliCollectionPage />} />
            <Route path="/brands/indusparquet" element={<BrandIndusparquet />} />
            <Route path="/brands/indusparquet/classico" element={<IndusparquetClassico />} />
            <Route path="/brands/indusparquet/largo" element={<IndusparquetLargo />} />
            <Route path="/brands/indusparquet/novo" element={<IndusparquetNovo />} />
            <Route path="/brands/indusparquet/solido" element={<IndusparquetSolido />} />
            <Route path="/brands/indusparquet/valor" element={<IndusparquetValor />} />
            <Route path="/brands/lauzon" element={<BrandLauzon />} />
            <Route path="/brands/lauzon/collection" element={<LauzonCollection />} />
            <Route path="/brands/lauzon/expert" element={<LauzonExpert />} />
            <Route path="/brands/mercier" element={<BrandMercier />} />
            <Route path="/brands/mercier/atmosphere" element={<MercierAtmosphere />} />
            <Route path="/brands/mercier/design-plus" element={<MercierDesignPlus />} />
            <Route path="/brands/mercier/elegancia" element={<MercierElegancia />} />
            <Route path="/brands/mercier/element" element={<MercierElement />} />
            <Route path="/brands/mercier/emblem" element={<MercierEmblem />} />
            <Route path="/brands/mercier/herringbone" element={<MercierHerringbone />} />
            <Route path="/brands/mercier/naked" element={<MercierNaked />} />
            <Route path="/brands/mercier/pro" element={<MercierPro />} />
            <Route path="/brands/mercier/origins" element={<MercierOrigins />} />
            <Route path="/brands/mercier/stellar" element={<MercierStellar />} />
            <Route path="/brands/mercier/source" element={<MercierSource />} />
            <Route path="/reclaimed-barnwood" element={<ReclaimedBarnwood />} />
            <Route path="/brands/olde-wood" element={<BrandOldeWood />} />
            <Route path="/brands/olde-wood/antique-reclaimed" element={<OldeWoodReclaimed />} />
            <Route path="/brands/olde-wood/traditional-plank" element={<OldeWoodTraditional />} />
            <Route path="/brands/olde-wood/metro" element={<OldeWoodMetro />} />
            <Route path="/brands/olde-wood/magnolia" element={<OldeWoodMagnolia />} />
            <Route path="/brands/olde-wood/venture" element={<OldeWoodVenture />} />
            <Route path="/brands/olde-wood/european" element={<OldeWoodEuropean />} />
            <Route path="/brands/olde-wood/select-prime" element={<OldeWoodSelectPrime />} />
            <Route path="/brands/olde-wood/rift-quartered" element={<OldeWoodRiftQuartered />} />
            <Route path="/brands/olde-wood/inspiration" element={<OldeWoodInspiration />} />
            <Route path="/brands/olde-wood/beyond-flooring" element={<OldeWoodBeyond />} />
            <Route path="/brands/ironwood" element={<BrandIronwood />} />
            <Route path="/brands/alterna" element={<BrandAlterna />} />
            <Route path="/brands/quick-step" element={<BrandQuickStep />} />
            <Route path="/brands/bella-cera" element={<BrandBellaCera />} />
            <Route path="/brands/bella-cera/villa-borghese-ii" element={<BellaCeraVillaBorgheseII />} />
            <Route path="/michigan-plank-flooring" element={<CustomFlooring />} />
            <Route path="/brands/quick-step/studio" element={<QuickStepStudio />} />
            <Route path="/brands/quick-step/naturetek-plus" element={<QuickStepNatureTEKPlus />} />
            <Route path="/brands/quick-step/naturetek-select" element={<QuickStepNatureTEKSelect />} />
            <Route path="/brands/quick-step/naturetek-premier" element={<QuickStepNatureTEKPremier />} />
            <Route path="/installation" element={<Installation />} />
            <Route path="/refinishing" element={<Refinishing />} />
            <Route path="/refinishing-cost" element={<RefinishingCost />} />
            <Route path="/installation-cost" element={<InstallationCost />} />
            <Route path="/inspections" element={<Inspections />} />
            <Route path="/subfloor-leveling" element={<SubfloorLeveling />} />
            <Route path="/product-specification" element={<ProductSpecification />} />
            <Route path="/projects/ann-arbor-subfloor" element={<ProjectAnnArbor />} />
            <Route path="/projects/ann-arbor-wide-white-oak" element={<ProjectAnnArborWhiteOak />} />
            <Route path="/projects/ann-arbor-tech" element={<ProjectAnnArborTech />} />
            <Route path="/projects/livonia-subfloor" element={<ProjectLivonia />} />
            <Route path="/environment-acclimation" element={<EnvironmentAcclimation />} />
            <Route path="/commercial" element={<Commercial />} />
            <Route path="/projects" element={<Projects />} />
            <Route path="/projects/howell-reclaimed-tobacco-pine" element={<ProjectBrightonReclamation />} />
            <Route path="/projects/brighton-mercier-hickory" element={<ProjectBrightonMercierHickory />} />
            <Route path="/projects/birmingham-viking-white-oak" element={<ProjectBirmingham kind="viking" />} />
            <Route path="/projects/birmingham-rift-quartered-walnut" element={<ProjectBirmingham kind="walnut" />} />
            <Route path="/projects/brighton-reclamation" element={<Navigate to="/projects/howell-reclaimed-tobacco-pine" replace />} />
            <Route path="/projects/lexington-reclaimed-oak" element={<ProjectLexingtonReclaimedOak />} />
            <Route path="/staircase-renovations" element={<StaircaseRenovations />} />
            <Route path="/projects/plymouth-custom-white-oak" element={<ProjectPlymouth />} />
            <Route path="/projects/northville-walnut" element={<ProjectNorthvilleWalnut />} />
            <Route path="/projects/harbor-springs-maple" element={<ProjectHarborSprings />} />
            <Route path="/lvp-installation" element={<LVPInstallation />} />
            <Route path="/laminate-installation" element={<WaterproofLaminateInstallation />} />
            <Route path="/alterna-tile-installation" element={<AlternaInstallation />} />
            <Route path="/luxury-vinyl-plank-flooring" element={<LuxuryVinylPlank />} />
            <Route path="/laminate-flooring" element={<LaminateFlooring />} />
            <Route path="/hardwood-flooring" element={<HardwoodFlooring />} />
            <Route path="/hardwood-flooring/:slug" element={<CityPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/water-damage-flooring" element={<WaterDamageFlooring />} />
            <Route path="/sand-and-finish" element={<SandAndFinish />} />
            <Route path="/floor-restoration" element={<FloorRestoration />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/dreamweaver-carpet" element={<DreamweaverCarpet />} />
            <Route path="/seo-issues-dashboard" element={<SeoIssuesDashboard />} />
            <Route path="/blog" element={<BlogIndex />} />
            <Route path="/blog/:slug" element={<ArticlePage />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<LegacyRedirectPage />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
